import type { ReactNode, MouseEventHandler } from 'react';
import { createRef, Component } from 'react';

import debounce from 'lodash/debounce';
import ImageGallery from 'react-image-gallery';

import { BpkButtonV2 } from '@skyscanner/backpack-web/bpk-component-button';
import ChevronLeftLg from '@skyscanner/backpack-web/bpk-component-icon/lg/chevron-left';
import ChevronRightLg from '@skyscanner/backpack-web/bpk-component-icon/lg/chevron-right';
import CloseButtonLg from '@skyscanner/backpack-web/bpk-component-icon/lg/close';
import ChevronLeftSm from '@skyscanner/backpack-web/bpk-component-icon/sm/chevron-left';
import ChevronRightSm from '@skyscanner/backpack-web/bpk-component-icon/sm/chevron-right';
import KeypadSm from '@skyscanner/backpack-web/bpk-component-icon/sm/keypad';
import PictureSm from '@skyscanner/backpack-web/bpk-component-icon/sm/picture';
import BpkText, {
  TEXT_COLORS,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import ImageItemNode from '../MediaGalleryItems/ImageItemNode';
import VideoItemNode from '../MediaGalleryItems/VideoItemNode';

import Filters from './Filters';
import MediaWall from './MediaWall';

import type {
  GalleryFilterItemClickEventHandler,
  ImagesGalleryCategory,
  ImagesGalleryItem,
} from '../types';
import type { TranslationKey } from '@shared/constants/translations';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';
import type { Nullable } from '@shared/types/utils';

import STYLES from './MediaGallery.module.scss';

import {
  REVIEW_IMAGES_CATEGORY,
  VIDEOS_CATEGORY,
} from '@hotels-hotel-detail/utils/hotel-images';
import { buildAdditionalInfoHotelMediaGalleryClicked } from '@hotels-hotel-detail/utils/mini-events/hotels-action';
import AdaptiveImage from '@shared/ui/common/AdaptiveImage';
import BpkBreakpoint, {
  withBpkBreakpoint,
  BREAKPOINTS,
} from '@shared/ui/common/BpkBreakpoint';
import PlayOverlay from '@shared/ui/common/PlayOverlay';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import {
  ACTION_TYPE,
  HOTEL_MEDIA_TYPE,
} from '@shared/utils-frontend/mini-events/constants';

type Props = {
  i18n: I18nService;
  items: ImagesGalleryItem[];
  categories?: ImagesGalleryCategory[];
  onClose: MouseEventHandler<HTMLButtonElement>;
  logGalleryViewed?: Function;
  startIndex?: number;
  showThumbnails?: boolean;
  isLoading?: boolean;
  infinite?: boolean;
  galleryView?: boolean;
  navigation?: ReactNode;
  isMobile?: boolean;
  miniEventTracker: MiniEventTracker;
  showTravelInfo?: boolean;
};

type ItemsInState = Array<
  ImagesGalleryItem & { originalClass?: string; thumbnailClass?: string }
>;

type State = {
  currentIndex: number;
  items: ItemsInState;
  selectedFilter?: Nullable<string>;
  currentCategoryImagesNumber: number;
  galleryView: boolean;
};

const defaultProps = {
  categories: [],
  startIndex: 0,
  showThumbnails: true,
  logGalleryViewed: () => null,
  infinite: true,
  galleryView: false,
  isLoading: false,
  showTravelInfo: true,
};

const cls = cssModules(STYLES);
const DEBOUNCE_TIME = 150;

const renderThumbInner = (item: ImagesGalleryItem) => {
  const GALLERY_THUMB_WIDTH = 80;
  const GALLERY_THUMB_HEIGHT = 80;

  return (
    <>
      <AdaptiveImage
        src={item.thumbnail}
        width={GALLERY_THUMB_WIDTH}
        height={GALLERY_THUMB_HEIGHT}
        altText=""
      />
      {item.category === VIDEOS_CATEGORY && (
        <PlayOverlay className={cls('MediaGallery__thumbnailVideoIcon')} />
      )}
    </>
  );
};

const MAX_IMAGES_TO_LOAD = 15;

class MediaGallery extends Component<Props, State> {
  static defaultProps = defaultProps;

  allItems: ItemsInState;

  galleryRef: any;

  videoItemRef: any;

  headerRef: any;

  mediaWallRef: any;

  imageOfCategory: any[];

  mediaWallItems: any;

  debouncedSetGalleryViewStyle: any;

  constructor(props: Props) {
    super(props);

    const { galleryView, items, startIndex } = this.props;
    const smallestItemsLength =
      startIndex! + 1 >= MAX_IMAGES_TO_LOAD
        ? startIndex! + 3
        : MAX_IMAGES_TO_LOAD;

    // TODO: assign images out of the constructor to allow image list changes
    // https://trello.com/c/qcqm7eKh/198-modify-image-gallery-component-to-allow-changes-in-image-list
    this.allItems = items.map(({ ...rest }) => ({
      originalClass: cls('MediaGallery__originalItem'),
      thumbnailClass: cls('MediaGallery__thumbnailItem'),
      ...rest,
    }));

    this.galleryRef = createRef();
    this.videoItemRef = createRef();
    this.headerRef = createRef();
    this.mediaWallRef = createRef();
    this.imageOfCategory = [];
    this.mediaWallItems = this.allItems;

    this.state = {
      currentIndex: startIndex!,
      // load limited image at a time
      items: this.allItems.slice(0, smallestItemsLength),
      currentCategoryImagesNumber: this.allItems.length,
      galleryView: !!galleryView,
      selectedFilter: null,
    };
  }

  componentDidMount() {
    const { items, logGalleryViewed, startIndex } = this.props;
    if (
      this.videoItemRef.current &&
      startIndex &&
      items &&
      items[startIndex].category === VIDEOS_CATEGORY
    ) {
      this.videoItemRef.current.onClick('play');
    }

    if (logGalleryViewed) {
      logGalleryViewed();
    }

    this.setGalleryViewStyle();
    this.debouncedSetGalleryViewStyle = debounce(
      this.setGalleryViewStyle,
      DEBOUNCE_TIME,
    );

    window.addEventListener('resize', () => {
      this.debouncedSetGalleryViewStyle();
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { items: preItems } = prevProps;
    const { items: curItems } = this.props;
    const preCategory = preItems?.[0]?.category;
    const curCategory = curItems?.[0]?.category;

    if (
      preCategory !== curCategory ||
      prevProps.items.length !== this.props.items.length
    ) {
      this.allItems = this.props.items.map(({ ...rest }) => ({
        originalClass: cls('MediaGallery__originalItem'),
        thumbnailClass: cls('MediaGallery__thumbnailItem'),
        ...rest,
      }));

      this.onFilterClick();
    }

    if (this.props.isLoading !== prevProps.isLoading) {
      this.setGalleryViewStyle();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', () => {
      this.debouncedSetGalleryViewStyle();
    });
  }

  setGalleryViewStyle = () => {
    if (this.headerRef.current && this.mediaWallRef.current) {
      const { offsetHeight: headerHeight } = this.headerRef.current;
      const viewPortHeight = window.innerHeight;
      this.mediaWallRef.current.style.top = `${headerHeight}px`;
      this.mediaWallRef.current.style.height = `${
        viewPortHeight - headerHeight
      }px`;
    }
  };

  onThumbnailClick = (_evt: any, index: number) => {
    this.setState({ currentIndex: index });
  };

  loadMore = (currentIndex: number) => {
    const imageData = this.imageOfCategory.length
      ? this.imageOfCategory
      : this.allItems;

    this.setState(
      (prevState) => ({
        items: [
          ...prevState.items,
          ...imageData.slice(
            prevState.items.length,
            prevState.items.length + MAX_IMAGES_TO_LOAD,
          ),
        ],
      }),
      () => {
        // avoid go back to startIndex when items changed
        this.galleryRef.slideToIndex(currentIndex);
      },
    );
  };

  onSlide = (currentIndex: number) => {
    const { startIndex } = this.props;
    this.setState({ currentIndex });

    if (Math.abs(currentIndex - startIndex!) > 0 && this.videoItemRef.current) {
      this.videoItemRef.current.onClick('pause');
    }

    const imageData = this.imageOfCategory.length
      ? this.imageOfCategory
      : this.allItems;

    if (
      this.state.items.length < imageData.length &&
      this.state.items.length - currentIndex <= 3
    ) {
      // if we're 3 or less away from the last slide load more
      this.loadMore(currentIndex);
    }
  };

  onFilterClick: GalleryFilterItemClickEventHandler = (category) => {
    let items;
    this.imageOfCategory = [];

    if (!category) {
      items = this.allItems.slice(0, MAX_IMAGES_TO_LOAD);
      this.mediaWallItems = this.allItems;
    } else {
      this.imageOfCategory = this.allItems.filter(
        (item) => item.category === category,
      );
      this.mediaWallItems = this.imageOfCategory;

      items = this.imageOfCategory.slice(0, MAX_IMAGES_TO_LOAD);
    }

    this.setState(
      {
        items,
        selectedFilter: category,
        currentIndex: 0,
        currentCategoryImagesNumber: !category
          ? this.allItems.length
          : this.imageOfCategory.length,
      },
      () => {
        this.galleryRef.slideToIndex(0);
      },
    );

    if (category === REVIEW_IMAGES_CATEGORY) {
      this.props.miniEventTracker.trackHotelsAction(
        ACTION_TYPE.HOTEL_MEDIA_GALLERY_CLICKED,
        buildAdditionalInfoHotelMediaGalleryClicked({
          hotelMediaType: HOTEL_MEDIA_TYPE.TRAVELLER_PHOTOS,
        }),
      );
    }
  };

  renderItem = (item: ImagesGalleryItem) => {
    const { showTravelInfo } = this.props;
    const { category, guestType, src, thumbnail, travelInfo } = item;
    if (src && thumbnail && category === VIDEOS_CATEGORY) {
      return <VideoItemNode src={src} poster={thumbnail} />;
    }

    return (
      <ImageItemNode
        src={src}
        guestType={guestType}
        travelInfo={showTravelInfo ? travelInfo : undefined}
      />
    );
  };

  renderLeftNav = (onClick: any, disabled: boolean) => {
    const { currentIndex, items } = this.state;
    const imageData = this.imageOfCategory.length
      ? this.imageOfCategory
      : this.allItems;

    return (
      <button
        type="button"
        className={cls('MediaGallery__leftNav')}
        aria-label={this.props.i18n.translate('aria_btn_previous')}
        disabled={
          disabled || (currentIndex === 0 && items.length < imageData.length)
        }
        onClick={() => {
          onClick();
          this.setState((prevState) => ({
            currentIndex: prevState.currentIndex - 1,
          }));
        }}
      >
        <BpkBreakpoint query={BREAKPOINTS.TABLET}>
          {(isUpToTablet: boolean) =>
            isUpToTablet ? <ChevronLeftSm /> : <ChevronLeftLg />
          }
        </BpkBreakpoint>
      </button>
    );
  };

  renderRightNav = (onClick: any, disabled: boolean) => (
    <button
      type="button"
      aria-label={this.props.i18n.translate('aria_btn_next')}
      className={cls('MediaGallery__rightNav')}
      disabled={disabled}
      onClick={() => {
        onClick();
        this.setState((prevState) => ({
          currentIndex: prevState.currentIndex + 1,
        }));
      }}
    >
      <BpkBreakpoint query={BREAKPOINTS.TABLET}>
        {(isUpToTablet: boolean) =>
          isUpToTablet ? <ChevronRightSm /> : <ChevronRightLg />
        }
      </BpkBreakpoint>
    </button>
  );

  toggleView = (currentIndex: number) => {
    this.setState((preState) => ({
      galleryView: !preState.galleryView,
    }));
    const { currentIndex: preIndex } = this.state;

    if (currentIndex !== preIndex) {
      this.setState(
        {
          items: this.mediaWallItems.slice(0, currentIndex + 3),
        },
        () => {
          this.galleryRef.slideToIndex(currentIndex);
        },
      );
    }
  };

  render() {
    const {
      categories,
      i18n,
      infinite,
      isLoading,
      isMobile,
      items,
      navigation,
      onClose,
      showThumbnails,
      startIndex,
    } = this.props;
    const totalItemsNumber = items?.length ?? 0;

    const {
      currentCategoryImagesNumber,
      currentIndex,
      galleryView,
      items: currentItems,
      selectedFilter,
    } = this.state;

    const viewButton: {
      icon: React.ReactElement;
      text: TranslationKey;
    } = {
      icon: galleryView ? <PictureSm /> : <KeypadSm />,
      text: galleryView
        ? 'HotelImageGallery_label_slideshowView'
        : 'HotelImageGallery_label_galleryView',
    };

    return (
      <>
        <div
          className={cls(
            'MediaGallery__galleryWrapper',
            galleryView && 'MediaGallery__galleryWrapper--galleryView',
          )}
        >
          <section ref={this.headerRef} className={cls('MediaGallery__header')}>
            <button
              aria-label={i18n.translate('Modal_close')}
              type="button"
              className={cls('MediaGallery__closeButton')}
              onClick={onClose}
            >
              <CloseButtonLg />
            </button>

            {navigation}
            <section className={cls('MediaGallery__imageFilter')}>
              {totalItemsNumber > 0 && (
                <Filters
                  onFilterClick={this.onFilterClick}
                  total={totalItemsNumber}
                  categories={categories}
                  selectedFilter={selectedFilter}
                  isLoading={isLoading}
                />
              )}

              {!isMobile && (
                <BpkButtonV2
                  onClick={() => this.toggleView(currentIndex)}
                  className={cls('MediaGallery__toggleViewBtn')}
                >
                  {viewButton.icon}
                  <BpkText tagName="p">
                    {i18n.translate(viewButton.text)}
                  </BpkText>
                </BpkButtonV2>
              )}
            </section>
          </section>

          <section
            className={cls(
              'MediaGallery__imageGalleryContainer',
              galleryView && 'MediaGallery__imageGalleryContainer--hide',
            )}
          >
            <ImageGallery
              ref={(i: any) => {
                this.galleryRef = i;
              }}
              items={currentItems}
              startIndex={startIndex}
              showFullscreenButton={false}
              showPlayButton={false}
              lazyLoad
              renderItem={this.renderItem}
              renderThumbInner={renderThumbInner}
              renderLeftNav={this.renderLeftNav}
              renderRightNav={this.renderRightNav}
              onThumbnailClick={this.onThumbnailClick}
              onSlide={this.onSlide}
              showThumbnails={showThumbnails}
              showNav={!isMobile}
              infinite={infinite}
            />
            {!isLoading && (
              <BpkText
                tagName="p"
                className={cls('MediaGallery__imageCount')}
                color={TEXT_COLORS.textPrimaryInverse}
              >
                <BpkText
                  className={cls(
                    currentCategoryImagesNumber < 10 &&
                      'MediaGallery__imageCount--smWidth',
                    currentCategoryImagesNumber >= 10 &&
                      currentCategoryImagesNumber < 100 &&
                      'MediaGallery__imageCount--baseWidth',
                    currentCategoryImagesNumber > 100 &&
                      'MediaGallery__imageCount--lgWidth',
                  )}
                >
                  {i18n.translate('HotelImageGallery_label_imageNumbering', {
                    current: currentIndex + 1,
                    total: currentCategoryImagesNumber,
                  })}
                </BpkText>
              </BpkText>
            )}
          </section>
        </div>
        {!isMobile && this.mediaWallItems && this.mediaWallItems.length > 0 && (
          <section
            className={cls(
              'MediaGallery__imageGalleryContainer',
              'MediaGallery__imageGalleryContainer--mediaWall',
              !galleryView && 'MediaGallery__imageGalleryContainer--hide',
            )}
            ref={this.mediaWallRef}
          >
            <section
              className={cls(
                'MediaGallery__imageGalleryContainer--mediaWallContainer',
              )}
            >
              <MediaWall
                items={this.mediaWallItems}
                toggleView={this.toggleView}
                selectedFilter={selectedFilter}
              />
            </section>
          </section>
        )}
      </>
    );
  }
}

export default withMiniEventTracker(
  withBpkBreakpoint(withI18n(MediaGallery), BREAKPOINTS.MOBILE, 'isMobile'),
);
